import { useCallback } from 'react'
import { MyQuizEnum } from '@types'
import styles from './GridUnitsLayout.module.scss'
import Card from '../unit-card/Card'
import DropBox from '../unit-card/DropBox'
import { useDrop } from 'react-dnd'
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
  ResponderProvided,
  DraggableProvided,
  DroppableProvided,
  DraggableStateSnapshot,
} from '@src/utils/componentCasts';
import { Quiz, Unit, UnitsByProgram } from '@src/api/graphql-codegen'

type Props = {
  units?: Unit[]
  source: number
  target?: string
  handleDrop?: (index: number, unit: Unit) => void
  handleLeftArrowAction?: (unit: Unit) => void
  handleRightArrowAction?: (unit: Unit) => void
  handleOpenUnit?: (unit: Unit) => void
  handleDnDOnCourseList?: (index: number, units: string[]) => void
  studyMode?: string
  quiz?: Quiz
  handleMovableUnit?: (movable: boolean) => void
  isMovable?: boolean
  showLabel?: boolean
  handleCardHovered?: (lessonId: string, hovered: boolean) => void;
  handleSelectedCenterCard?: (centerCard: boolean) => void
  cardHovered?: boolean;
  hoveredLessonId?: string;
  programs?: UnitsByProgram[];
}

const reorder = (list: any, startIndex: number, endIndex: number) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);

  return result;
};

function GridLessonsLayout(props: Props) {
  const { 
    showLabel, units, source, target, studyMode, quiz, handleDrop, handleLeftArrowAction, handleRightArrowAction, handleOpenUnit, handleDnDOnCourseList, isMovable, handleMovableUnit,
    handleCardHovered, handleSelectedCenterCard, cardHovered, hoveredLessonId, programs
   } = props
  const onDrop = (unit: Unit) => {
    handleDrop && handleDrop(source, unit)
  }
  const handleDragEnd = useCallback((result: DropResult, provided?: ResponderProvided) => {
    if (!result.destination) {
      return;
    }
    if (result.destination.index === result.source.index) {
      return;
    }
    const sourceIndex = result.source.index;
    const destIndex = result.destination.index;

    const updateLessons = reorder(units, sourceIndex, destIndex) as Unit[]
    const lessonIds = updateLessons.map((item: Unit) => {
      return item.id
    })
    if (handleDnDOnCourseList) {
      handleDnDOnCourseList(source, lessonIds)
    }
  }, [source, units, handleDnDOnCourseList]);

  const [{ canDrop }, drop] = useDrop({
    accept: "type",
    drop: onDrop,
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
    }),
  })

  if (source === 0) {
    return (
      <div ref={drop} className={`${studyMode === MyQuizEnum.MyQuizzes ? styles.unitsDivStudyMode : styles.unitsDiv} ${canDrop ? styles.canDropbackground : ''}`}>
        {units?.map((unit, index) => (
          <Card
            key={index}
            type={"type"}
            source={source}
            target={target}
            unit={unit}
            isDragable={studyMode === MyQuizEnum.QuizBuilder}
            handleLeftArrowAction={() => handleLeftArrowAction && handleLeftArrowAction(unit)}
            handleRightArrowAction={() => handleRightArrowAction && handleRightArrowAction(unit)}
            handleOpenUnit={handleOpenUnit}
            studyMode={studyMode}
            handleMovableUnit={handleMovableUnit}
            showLabel={showLabel}
            cardHovered={cardHovered}
            hoveredLessonId={hoveredLessonId}
            index={index+1}
            handleSelectedCenterCard={handleSelectedCenterCard}
            handleCardHovered={handleCardHovered}
            programs={programs}
          />
        ))}
      </div>
    )
  }

  return (
    <div ref={drop} className={styles.normalDiv }>
      <DropBox
        accept={"type"}
        source={source}
        onDrop={(item) => handleDrop && handleDrop(source, item)}
        quiz={quiz}
        isMovable={isMovable ?? false}
      />
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="droppableLesson" type="StudyPlanItems">
          {(droppableProvided: DroppableProvided) => (
            <div>
              <div
                ref={droppableProvided.innerRef}
                {...droppableProvided.droppableProps}
              >
                {units?.map((unit, index) => (
                  <Draggable
                    key={unit.id}
                    draggableId={unit.id}
                    index={index}
                  >
                    {(
                      draggableProvided: DraggableProvided,
                      snapshot: DraggableStateSnapshot
                    ) => (
                      <div
                        ref={draggableProvided.innerRef}
                        {...draggableProvided.draggableProps}
                      >
                        <Card 
                          draggableProvided={draggableProvided} snapshot={snapshot} key={index} type={"type"} 
                          source={source} target={target} unit={unit} isDragable={true}
                          handleLeftArrowAction={() => handleLeftArrowAction && handleLeftArrowAction(unit)}
                          handleRightArrowAction={() => handleRightArrowAction && handleRightArrowAction(unit)}
                          index={index}
                        />
                      </div>
                    )}
                  </Draggable>
                ))}
              </div>
              {droppableProvided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    </div>
  )
}

export default GridLessonsLayout